import { formatIsoDateStringForTable } from './format';
import type { IIsoDateStringDateRange } from './types';
import { addToIsoDateString, isIsoDateStringAfter, isIsoDateStringValid } from './utils';

/**
 * Converts start and end date IsoDateStrings to a validated IIsoDateStringDateRange.
 * Returns a date range that ranges from the start date, to the end date, inclusive,
 * or throws an error if dates are invalid or the end date occurs before the start date.
 * Use with `getStartOfDayFromIsoDateString()` and `getEndOfDayFromIsoDateString()` to
 * get ISO datetime strings with timezone offset representing the start of the start date
 * and end of the end date.
 */
export function toIsoDateStringDateRange(
  startDateString: string,
  endDateString: string
): IIsoDateStringDateRange {
  if (!isIsoDateStringValid(startDateString) || !isIsoDateStringValid(endDateString)) {
    throw new Error(
      `Cannot create date range with invalid dates. Received (${startDateString}, ${endDateString})`
    );
  }
  if (isIsoDateStringAfter(startDateString, endDateString)) {
    throw new Error(`Date range is invalid, ${startDateString} must be before ${endDateString}`);
  }
  return {
    start: startDateString,
    end: endDateString,
  };
}

/**
 * Return array of formatted days in dateRange including last day.
 * E.g. ['Tue 20', 'Wed 21', 'Thu 22']
 */
export function getFormattedDaysFromIsoDateStringDateRange(
  dateRange: IIsoDateStringDateRange
): string[] {
  const { start: rangeStart, end: rangeEnd } = dateRange;
  if (isIsoDateStringAfter(rangeStart, rangeEnd)) {
    throw new Error(`Date range is invalid, ${rangeStart} must be before ${rangeEnd}`);
  }

  const days: string[] = [];
  let day = rangeStart;
  while (!isIsoDateStringAfter(day, rangeEnd)) {
    days.push(formatIsoDateStringForTable(day));
    day = addToIsoDateString(day, { days: 1 });
  }
  return days;
}
